import { useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"

import routes from "@/routes"
import { useShadcnInsetBreadcrumbContext } from "@/contexts/ShadcnInsetBreadcrumbContext"
import {
  getBreadcrumbTrail,
  type BreadcrumbCrumb,
} from "@/utils/breadcrumbPageTitle"
import type { RouteConfigArray } from "@/types/routes"

/**
 * Replace the inset breadcrumb trail for the current page (cleared on unmount).
 */
export function useShadcnInsetBreadcrumbTrail(trail: BreadcrumbCrumb[] | null) {
  const { setTrailOverride } = useShadcnInsetBreadcrumbContext()

  useEffect(() => {
    setTrailOverride(trail)
    return () => setTrailOverride(null)
  }, [trail, setTrailOverride])
}

export type DynamicPageTitleOptions = {
  /**
   * Replace route-derived parent crumbs (e.g. Strategies / Reports / Simulator)
   * with a single parent — used when opening entity pages from the Workspace hub.
   */
  parent?: BreadcrumbCrumb | null
}

/**
 * Keeps route-based parent crumbs and sets the current page label (e.g. simulation title).
 */
export function useShadcnInsetDynamicPageTitle(
  title: string | null | undefined,
  options?: DynamicPageTitleOptions
) {
  const { pathname, search } = useLocation()
  const parent = options?.parent ?? null

  const trail = useMemo(() => {
    const base = getBreadcrumbTrail(routes as RouteConfigArray, pathname, search)
    const trimmed = title?.trim()
    if (parent) {
      return trimmed ? [parent, { label: trimmed }] : [parent]
    }
    if (!trimmed) {
      return base
    }
    if (base.length === 0) {
      return [{ label: trimmed }]
    }
    const parents = base.slice(0, -1)
    return [...parents, { label: trimmed }]
  }, [pathname, search, title, parent])

  useShadcnInsetBreadcrumbTrail(trail)
}
